import { getCategoryLabel } from "@/lib/autocompleteCategoryLabel"
import { absoluteHref } from "@/lib/embed/absoluteHref"
import {
  dedupeRecentSearches,
  normalizeRecentSearchLink,
} from "@/lib/recentSearches"
import type { RecentSearch } from "@/db"
import type { SavedSearchItem } from "@/types/savedSearch"

const DB_NAME = "bpp-local"
const RECENT_SEARCHES_STORE = "recentSearches"
const SAVED_SEARCH_DISPLAY_LIMIT = 3
const LOCAL_RECENTS_KEY = "bpp-embed-recents"

/** A default-state row: what the widget shows before the visitor types. */
export type Suggestion = {
  key: string
  title: string
  meta: string
  section: string
  href: string
}

type SavedSearchRow = Pick<SavedSearchItem, "SavedSearchId" | "Name" | "SearchUrl">

/** A recent search the widget recorded itself, on the host page's own origin. */
type StoredRecent = Omit<RecentSearch, "id">

/**
 * Browser storage is keyed on origin, not URL path. Reverse-proxy tenants serve
 * the search app under the marketing apex (`nwmls.com/homes-for-sale/*`), so
 * both share one `bpp-local` database and one session cookie.
 *
 * Subdomain tenants do not, and there is no way to reach across. Those fall back
 * to `localStorage` on the host page — recents the widget recorded itself, which
 * is strictly less than the app's list but is at least the visitor's own history
 * on this site. Saved searches have no fallback: they need the session cookie,
 * which would be a third-party cookie there and is blocked outright by Safari
 * and Firefox.
 */
export function sharesOriginWithSearchApp(searchBaseUrl: string): boolean {
  if (typeof window === "undefined") {
    return false
  }
  try {
    return new URL(searchBaseUrl).origin === window.location.origin
  } catch {
    return false
  }
}

function openLocalDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest
    try {
      // Deliberately versionless. The search app owns this schema (src/db.ts)
      // and bumps the version over time, while this bundle sits cached on a
      // customer's marketing site for months. Naming a version here would throw
      // VersionError the first time the app moved ahead of the embed.
      request = indexedDB.open(DB_NAME)
    } catch {
      resolve(null)
      return
    }
    request.onupgradeneeded = () => {
      // The database does not exist yet. A versionless open would create it
      // empty at version 1 and leave the search app to upgrade from a version
      // we invented — across a store that also holds the visitor's favorites.
      // Aborting rolls the creation back; callers fall through to localStorage.
      request.transaction?.abort()
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
}

async function readRecentSearches(): Promise<RecentSearch[]> {
  const db = await openLocalDb()
  if (!db) {
    return []
  }
  try {
    // Absent until the visitor has used the search app at least once.
    if (!db.objectStoreNames.contains(RECENT_SEARCHES_STORE)) {
      return []
    }
    return await new Promise<RecentSearch[]>((resolve) => {
      const request = db
        .transaction(RECENT_SEARCHES_STORE, "readonly")
        .objectStore(RECENT_SEARCHES_STORE)
        .getAll()
      request.onsuccess = () => resolve(request.result ?? [])
      request.onerror = () => resolve([])
    })
  } catch {
    return []
  } finally {
    db.close()
  }
}

function isStoredRecent(value: unknown): value is StoredRecent {
  if (typeof value !== "object" || value === null) {
    return false
  }
  const row = value as Record<string, unknown>
  return (
    typeof row.link === "string" &&
    typeof row.resultDisplayName === "string" &&
    typeof row.categoryName === "string" &&
    typeof row.resultId === "string" &&
    typeof row.timestamp === "number"
  )
}

/**
 * Anything on the host page can write to this key, and every field ends up in
 * rendered text or a redirect target, so rows are shape-checked on the way out.
 * The href itself cannot leave the tenant origin — `absoluteHref` always builds
 * `searchBaseUrl + "/..."`.
 */
export function readLocalRecents(): StoredRecent[] {
  try {
    const raw = window.localStorage.getItem(LOCAL_RECENTS_KEY)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isStoredRecent) : []
  } catch {
    return []
  }
}

export function writeLocalRecent(entry: StoredRecent): void {
  try {
    const next = dedupeRecentSearches([entry, ...readLocalRecents()])
    window.localStorage.setItem(LOCAL_RECENTS_KEY, JSON.stringify(next))
  } catch {
    // Private browsing, a disabled store, or quota. Losing a recent search is
    // not worth failing the redirect the visitor actually asked for.
  }
}

function isSavedSearchRow(value: unknown): value is SavedSearchRow {
  if (typeof value !== "object" || value === null) {
    return false
  }
  const row = value as Record<string, unknown>
  return (
    typeof row.SavedSearchId === "string" &&
    typeof row.Name === "string" &&
    typeof row.SearchUrl === "string"
  )
}

async function readSavedSearches(searchBaseUrl: string): Promise<SavedSearchRow[]> {
  try {
    // Same-origin, so the WorkOS session cookie rides along without opting into
    // credentials. Signed out answers 401; a proxy that forwards page routes but
    // not `/api/*` answers with the customer's own HTML, which fails the JSON
    // parse. Both mean "nothing to show".
    const response = await fetch(`${searchBaseUrl}/api/saved-search`)
    if (!response.ok) {
      return []
    }
    const data: unknown = await response.json()
    if (!Array.isArray(data)) {
      return []
    }
    return data.filter(isSavedSearchRow).slice(0, SAVED_SEARCH_DISPLAY_LIMIT)
  } catch {
    return []
  }
}

/**
 * `SearchUrl` is stored absolute and can carry a host from another environment,
 * so keep only the path and re-base it on the tenant we are embedding for.
 */
export function savedSearchHref(
  searchBaseUrl: string,
  row: SavedSearchRow
): string | null {
  try {
    const url = new URL(row.SearchUrl, searchBaseUrl)
    url.searchParams.set("savedSearchName", row.Name)
    return absoluteHref(searchBaseUrl, `${url.pathname}${url.search}`)
  } catch {
    return null
  }
}

export function buildSuggestions(
  searchBaseUrl: string,
  saved: SavedSearchRow[],
  recents: RecentSearch[]
): Suggestion[] {
  const suggestions: Suggestion[] = []

  for (const row of saved) {
    const href = row.Name.trim() ? savedSearchHref(searchBaseUrl, row) : null
    if (href) {
      suggestions.push({
        key: `saved-${row.SavedSearchId}`,
        title: row.Name,
        meta: "Saved search",
        section: "Recent Saved Searches",
        href,
      })
    }
  }

  for (const row of dedupeRecentSearches(recents)) {
    suggestions.push({
      key: `recent-${row.id ?? `${row.timestamp}-${row.resultId}`}`,
      title: row.resultDisplayName,
      meta: getCategoryLabel(row.categoryName),
      section: "Recent Searches",
      href: absoluteHref(searchBaseUrl, normalizeRecentSearchLink(row.link)),
    })
  }

  return suggestions
}

export async function loadSuggestions(searchBaseUrl: string): Promise<Suggestion[]> {
  if (!sharesOriginWithSearchApp(searchBaseUrl)) {
    return buildSuggestions(searchBaseUrl, [], readLocalRecents())
  }
  const [saved, recents] = await Promise.all([
    readSavedSearches(searchBaseUrl),
    readRecentSearches(),
  ])
  // The app's own list wins. Local rows are only a holding pen for searches made
  // before the shared database existed, so they stop mattering once it does.
  return buildSuggestions(
    searchBaseUrl,
    saved,
    recents.length > 0 ? recents : readLocalRecents()
  )
}

/** Resolves false when there is no shared store to write to. */
async function writeIndexedRecent(row: StoredRecent): Promise<boolean> {
  const db = await openLocalDb()
  if (!db) {
    return false
  }
  try {
    if (!db.objectStoreNames.contains(RECENT_SEARCHES_STORE)) {
      return false
    }
    return await new Promise<boolean>((resolve) => {
      const tx = db.transaction(RECENT_SEARCHES_STORE, "readwrite")
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => resolve(false)
      tx.onabort = () => resolve(false)
      tx.objectStore(RECENT_SEARCHES_STORE).add(row)
    })
  } catch {
    return false
  } finally {
    db.close()
  }
}

/**
 * Mirrors the write the search app makes on select, so a search started from the
 * marketing page shows up in recents everywhere else. Awaited by the caller
 * because the redirect that follows would otherwise cancel the transaction.
 */
export async function recordRecentSearch(
  searchBaseUrl: string,
  entry: Omit<RecentSearch, "id" | "timestamp">
): Promise<void> {
  const row: StoredRecent = { ...entry, timestamp: Date.now() }

  if (sharesOriginWithSearchApp(searchBaseUrl) && (await writeIndexedRecent(row))) {
    return
  }

  // No shared store to write to: a different origin, private browsing, or the
  // visitor has not used the search app yet so its database has no such store.
  writeLocalRecent(row)
}
